import { formatCalendarDateIso, parseUtcDate } from './dateFormat';

export interface CalendarParts {
  year: number;
  month: number;
  day: number;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** yyyy-mm-dd from calendar parts (civil Gregorian date). */
export function toIsoDate(parts: CalendarParts): string {
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

/** Days in month (1–12), Gregorian. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Clamp day for month/year (e.g. 29 Feb → 28 in non-leap years). */
export function clampDayForMonth(year: number, month: number, day: number): number {
  return Math.min(day, daysInMonth(year, month));
}

export function getCalendarPartsInAppTz(date: string | Date): CalendarParts | null {
  const dateObj = parseUtcDate(date);
  if (!dateObj) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(dateObj);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const d = parts.find((p) => p.type === 'day')?.value;
  if (!y || !m || !d) return null;
  return { year: parseInt(y, 10), month: parseInt(m, 10), day: parseInt(d, 10) };
}

function calendarDaysBetween(earlierYmd: string, laterYmd: string): number {
  const [y1, m1, d1] = earlierYmd.split('-').map(Number);
  const [y2, m2, d2] = laterYmd.split('-').map(Number);
  const t1 = Date.UTC(y1, m1 - 1, d1);
  const t2 = Date.UTC(y2, m2 - 1, d2);
  return Math.round((t2 - t1) / 86400000);
}

function splitYmd(ymd: string): { y: number; m: number; d: number } {
  const [y, m, d] = ymd.split('-').map(Number);
  return { y, m, d };
}

/**
 * Next calendar date matching (month/day) on or after today (today if it is the anniversary).
 * Respects anchor year floor (e.g. engagement year) so we do not show a date before the first occurrence.
 */
export function getNextAnnualOccurrenceOnOrAfter(
  todayYmd: string,
  month: number,
  day: number,
  anchorYearFloor: number
): string {
  const [ty] = todayYmd.split('-').map(Number);
  let y = ty;
  let d = clampDayForMonth(y, month, day);
  let candidate = `${y}-${pad2(month)}-${pad2(d)}`;
  if (candidate < todayYmd) {
    y = ty + 1;
    d = clampDayForMonth(y, month, day);
    candidate = `${y}-${pad2(month)}-${pad2(d)}`;
  }
  const cy = parseInt(candidate.slice(0, 4), 10);
  if (cy < anchorYearFloor) {
    const d0 = clampDayForMonth(anchorYearFloor, month, day);
    return `${anchorYearFloor}-${pad2(month)}-${pad2(d0)}`;
  }
  return candidate;
}

export interface YearsOfServiceResult {
  /** Completed full years since engagement (HR-style). */
  years: number;
  /** Calendar months since the last service anniversary (0–11). */
  monthsSinceAnniversary: number;
}

export function getYearsOfService(dateOfEngagement: string | Date | null | undefined): YearsOfServiceResult | null {
  if (dateOfEngagement == null || dateOfEngagement === '') return null;
  const engage = getCalendarPartsInAppTz(dateOfEngagement);
  if (!engage) return null;
  const todayYmd = formatCalendarDateIso(new Date());
  const engageYmd = toIsoDate(engage);

  if (todayYmd < engageYmd) {
    return { years: 0, monthsSinceAnniversary: 0 };
  }

  const { y: ty, m: tm, d: td } = splitYmd(todayYmd);
  let annY = ty;
  let annD = clampDayForMonth(annY, engage.month, engage.day);
  let annThisYear = `${annY}-${pad2(engage.month)}-${pad2(annD)}`;
  if (annThisYear > todayYmd) {
    annY = ty - 1;
    annD = clampDayForMonth(annY, engage.month, engage.day);
    annThisYear = `${annY}-${pad2(engage.month)}-${pad2(annD)}`;
  }
  if (annThisYear < engageYmd) {
    return { years: 0, monthsSinceAnniversary: 0 };
  }

  const years = Math.max(0, annY - engage.year);

  const { y: ly, m: lm, d: ld } = splitYmd(annThisYear);
  let monthsSince = (ty - ly) * 12 + (tm - lm);
  if (td < ld) monthsSince--;
  monthsSince = Math.max(0, monthsSince);

  return { years, monthsSinceAnniversary: monthsSince };
}

/**
 * Days until the next occurrence of the anchor's month/day (0 = today).
 * Anchor year bounds recurring logic for engagement (cannot celebrate before hire).
 */
export function getDaysUntilNextAnnualEvent(
  anchorIso: string | Date,
  anchorYearFloor: number
): number | null {
  const anchor = getCalendarPartsInAppTz(anchorIso);
  if (!anchor) return null;
  const todayYmd = formatCalendarDateIso(new Date());
  const next = getNextAnnualOccurrenceOnOrAfter(
    todayYmd,
    anchor.month,
    anchor.day,
    anchorYearFloor
  );
  return calendarDaysBetween(todayYmd, next);
}

/** Days until next birthday from date of birth (anchor year floor = birth year). */
export function getDaysUntilBirthday(dateOfBirth: string | Date | null | undefined): number | null {
  if (dateOfBirth == null || dateOfBirth === '') return null;
  const birth = getCalendarPartsInAppTz(dateOfBirth);
  if (!birth) return null;
  return getDaysUntilNextAnnualEvent(dateOfBirth, birth.year);
}

/** Days until next work anniversary (engagement date recurrence). */
export function getDaysUntilWorkAnniversary(dateOfEngagement: string | Date | null | undefined): number | null {
  if (dateOfEngagement == null || dateOfEngagement === '') return null;
  const engage = getCalendarPartsInAppTz(dateOfEngagement);
  if (!engage) return null;
  return getDaysUntilNextAnnualEvent(dateOfEngagement, engage.year);
}
